import _ from 'underscore';
import { useRef } from 'react';
import PropTypes from 'prop-types';

import { useOnceEffect, getSharedValues } from './ab-testing-event-log';

export const CUSTOMER_JOURNEY_PRODUCT = {
  None: null,
  SC: 'SC',
  ESC: 'ESC',
  Expert: 'Expert',
  StreamlinedSignup: 'StreamlinedSignup',
  PMaxLite: 'PMaxLite',
};

export const CUSTOMER_JOURNEY_SUB_PRODUCT = {
  None: null,
  SubscriptionAds: 'SubscriptionAds',
  M365: 'M365',
};

export const CUSTOMER_JOURNEY_INTERFACE = {
  Web: 'Web',
  Mobile: 'Mobile',
  Editor: 'Editor',
  API: 'API',
};

export const userAgent = _.get(navigator, 'userAgent') || _.get(navigator, 'vendor') || _.get(window, 'opera');

export function CustomerJourneyProvider(props) {
  const {
    children,
    serverContext,
    postEventLog,
    logWarning,
    flights,
    postFlightLog,
    product,
    subProduct,
    customerInterface,
  } = props;

  // logging is asynchronously created on app launch
  // therefore logWarning and postEventLog are optional, however will disable shouldPost
  const shouldPost = props.shouldPost && _.isFunction(logWarning) && _.isFunction(postEventLog);
  const shouldPostFlights = props.shouldPost && _.isFunction(postFlightLog);

  // We don't want to re-log events if "eventValues" or "postEventLog" changes
  // therefore store both values as ref
  const eventValues = useRef({});
  const postEventLogRef = useRef({});
  const sharedValues = getSharedValues(serverContext);
  const UniqueId = sharedValues.SessionId;
  eventValues.current = {
    Mode: sharedValues.Mode,
    CID: sharedValues.CID,
    AID: sharedValues.AID,
  };

  postEventLogRef.current = {
    postEvent: (EventName, EventValue) => postEventLog({
      UniqueId,
      EventName: `CustomerJourney:${EventName}`,
      EventValue,
    }),
    logValidationError: (error) => {
      const message = `CustomerJourneyProvider validation error: ${error}`;
      logWarning(message);
    },
    postFlight: postFlightLog,
  };

  useOnceEffect(() => {
    if (shouldPost) {
      postEventLogRef.current.postEvent('AppLoad', {
        ...eventValues.current,
        Product: product,
        SubProduct: subProduct,
        Interface: customerInterface,
        OS: userAgent,
        ResolutionWidth: _.get(window, 'innerWidth'),
        ResolutionHeight: _.get(window, 'innerHeight'),
        PixelRatio: _.get(window, 'devicePixelRatio'),
      });
      return true;
    }
    return false;
  });

  useOnceEffect(() => {
    if (shouldPostFlights) {
      postEventLogRef.current.postFlight(flights);
      return true;
    }
    return false;
  });

  return children;
}

CustomerJourneyProvider.propTypes = {
  shouldPost: PropTypes.bool,
  logWarning: PropTypes.func,
  postEventLog: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  serverContext: PropTypes.shape({}),
  flights: PropTypes.arrayOf(PropTypes.shape({})),
  postFlightLog: PropTypes.func,
  product: PropTypes.string,
  subProduct: PropTypes.string,
  customerInterface: PropTypes.string,
};

CustomerJourneyProvider.defaultProps = {
  shouldPost: false,
  serverContext: undefined,
  logWarning: null,
  postEventLog: null,
  flights: [],
  postFlightLog: null,
  product: CUSTOMER_JOURNEY_PRODUCT.None,
  subProduct: CUSTOMER_JOURNEY_SUB_PRODUCT.None,
  customerInterface: CUSTOMER_JOURNEY_INTERFACE.Web,
};
